import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { usePageTracking } from './hooks/usePageTracking';
import Header from './layouts/Header';
import Footer from './layouts/Footer';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import PortfolioPage from './pages/PortfolioPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AdvertisingPage from './pages/AdvertisingPage';
import CareersPage from './pages/CareersPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import OurTeamPage from './pages/OurTeamPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Fetch site_settings once on app boot and inject the brand primary color
 * as a CSS custom property on :root so Tailwind's `primary` utilities and
 * any inline styles that reference var(--color-primary) stay in sync with
 * whatever the admin has configured in /admin/sitesettings.
 */
async function applyBrandColors() {
  try {
    const { data } = await axios.get(`${API_BASE}/settings/public`);
    const settings = data?.data;
    if (settings?.primaryColor) {
      document.documentElement.style.setProperty('--color-primary', settings.primaryColor);
    }
    if (settings?.secondaryColor) {
      document.documentElement.style.setProperty('--color-secondary', settings.secondaryColor);
    }
  } catch {
    // Non-fatal — fall back to the Tailwind defaults defined in tailwind.config.js
  }
}

// Fire once at module load time (before first render)
applyBrandColors();

function App() {
  const location = useLocation();

  // Track page views for analytics (lightweight, non-blocking)
  usePageTracking();

  // Reinitialize AOS completely on every route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (typeof window !== 'undefined' && (window as any).AOS) {
      // Remove all existing AOS attributes and classes
      document.querySelectorAll('[data-aos]').forEach((element) => {
        element.classList.remove('aos-init', 'aos-animate');
      });

      // Wait for DOM to settle, then reinitialize AOS
      const timer = setTimeout(() => {
        if ((window as any).AOS) {
          // Reinitialize AOS from scratch
          (window as any).AOS.init({
            duration: 800,
            once: false,
            offset: 100,
            disable: false,
            anchorPlacement: 'top-bottom',
            mirror: false,
            delay: 0,
          });

          // Force refresh to detect all elements
          (window as any).AOS.refresh();
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main key={location.pathname} className="flex-grow pt-20">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/our-team" element={<OurTeamPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/advertising" element={<AdvertisingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
